import {
	ClickAwayListener,
	IconButton,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { Info } from "lucide-react";
import {
	type FC,
	type ReactElement,
	useLayoutEffect,
	useRef,
	useState,
} from "react";
import { LIGHT_BLUE_MYPLANN } from "web_myplann/Utils/constants";

type DataRowTooltipProps = {
	isOpen: boolean;
	title: string;
	setIsOpen: (open: boolean) => void;
	onClick?: () => void;
};

const DataRowTooltip: FC<DataRowTooltipProps> = ({
	isOpen,
	title,
	setIsOpen,
	onClick,
}) => {
	const theme = useTheme();

	return (
		<Tooltip
			PopperProps={{
				disablePortal: true,
				modifiers: [{ name: "offset", options: { offset: [0, -20] } }],
			}}
			open={isOpen}
			disableTouchListener
			disableFocusListener
			disableHoverListener
			placement="top"
			title={title}
			componentsProps={{
				tooltip: {
					sx: {
						bgcolor: theme.palette.grey[100],
						color: theme.palette.common.black,
					},
				},
			}}
		>
			<IconButton
				onClick={() => {
					setIsOpen(true);
					onClick?.();
				}}
				sx={{ padding: "0 8px" }}
			>
				<Info size={20} color={theme.palette.primary.main} />
			</IconButton>
		</Tooltip>
	);
};

type DataRowProps = {
	label: string;
	value?: string | number | null | undefined;
	valueTooltip?: string;
	link?: string;
	element?: ReactElement;
	labelTooltip?: string;
	onLabelTooltipClick?: () => void;
	hideIfEmpty?: boolean;
};

export const DataRow: FC<DataRowProps> = ({
	label,
	value = "",
	valueTooltip,
	link,
	element,
	labelTooltip,
	onLabelTooltipClick,
	hideIfEmpty = true,
}) => {
	const theme = useTheme();

	const [isLabelTooltipOpen, setLabelTooltipOpen] = useState(false);
	const [isValueTooltipOpen, setValueTooltipOpen] = useState(false);

	const [alignItems, setAlignItems] = useState<"center" | "flex-start">(
		"center",
	);

	const valueRef = useRef<HTMLSpanElement>(null);

	useLayoutEffect(() => {
		if (valueRef.current) {
			const computed = window.getComputedStyle(valueRef.current);
			const lineHeight = Number.parseFloat(computed.lineHeight);
			const height = valueRef.current.offsetHeight;
			const lines = Math.round(height / lineHeight);
			setAlignItems(lines > 1 ? "flex-start" : "center");
		}
	}, [value]);

	if (
		hideIfEmpty &&
		(value == null || value === "") &&
		!element &&
		!labelTooltip &&
		!onLabelTooltipClick
	) {
		return null;
	}

	const displayLabelTooltip = labelTooltip || onLabelTooltipClick;

	return (
		<Stack direction="row" alignItems={alignItems} py={0.4}>
			<ClickAwayListener onClickAway={() => setLabelTooltipOpen(false)}>
				<Stack
					direction="row"
					alignItems="center"
					sx={{ flex: "0 1 auto", maxWidth: "50%", minWidth: 0, mr: 2 }}
				>
					<Typography variant="body1" color={theme.palette.grey[500]}>
						{label}
					</Typography>

					{displayLabelTooltip && (
						<DataRowTooltip
							isOpen={isLabelTooltipOpen}
							title={labelTooltip ?? ""}
							setIsOpen={setLabelTooltipOpen}
							onClick={() => onLabelTooltipClick?.()}
						/>
					)}
				</Stack>
			</ClickAwayListener>

			<ClickAwayListener onClickAway={() => setValueTooltipOpen(false)}>
				<Stack
					direction="row"
					alignItems={alignItems}
					gap={0.4}
					sx={{
						flex: "1 1 0%",
						justifyContent: "flex-end",
						minWidth: 0,
					}}
				>
					{!element && (
						<Typography
							variant="body2"
							color={link ? LIGHT_BLUE_MYPLANN : theme.palette.grey[700]}
							ref={valueRef}
							sx={{
								flex: 1,
								minWidth: 0,
								textAlign: "right",
								whiteSpace: "normal",
								wordBreak: "break-word",
							}}
							onClick={() => {
								if (link) {
									window.open(link, "_blank", "noopener,noreferrer");
								}
							}}
						>
							{value}
						</Typography>
					)}

					{element}

					{valueTooltip && (
						<DataRowTooltip
							isOpen={isValueTooltipOpen}
							title={valueTooltip}
							setIsOpen={setValueTooltipOpen}
						/>
					)}
				</Stack>
			</ClickAwayListener>
		</Stack>
	);
};
